import type { ReactNode } from 'react';
import { Input } from '@/shared/ui/input';
import { Select } from '@/shared/ui/select';
import type { DocumentDraft } from '@/modules/agreements/platform/pages/document-create-page/types';
import type { AgreementAudience, AgreementRequiredAction } from '@/api/schemas/agreements.types';

const AUDIENCE_OPTIONS = [
  { value: 'tenant', label: 'Tenant (vendor)' },
  { value: 'customer', label: 'Customer' },
  { value: 'both', label: 'Both' },
];

const ACTION_OPTIONS = [
  { value: 'acknowledgment', label: 'Acknowledgment' },
  { value: 'consent', label: 'Consent' },
  { value: 'signature', label: 'Signature' },
];

/**
 * Step 1 — the document's stable identity.
 *
 * A document is the identity; its CONTENT is published as versions. The key is
 * permanent and has no hyphens, so it is validated against the API's own pattern
 * here rather than being discovered to be wrong two steps later.
 *
 * The audience and action chosen here are not cosmetic: they decide whether step
 * 3 must demand an explicit verification policy, so the hint says so.
 */
export function DetailsStep(props: {
  draft: DocumentDraft;
  onChange: (patch: Partial<DocumentDraft>) => void;
}): ReactNode {
  const { draft, onChange } = props;
  const keyTouched = draft.documentKey.length > 0;
  const keyValid = /^[a-z][a-z0-9_]{1,63}$/.test(draft.documentKey);

  return (
    <div className="space-y-4">
      <Input
        label="Document key"
        value={draft.documentKey}
        onChange={(event) => onChange({ documentKey: event.target.value })}
        hint="Lowercase letters, digits and underscores. No hyphens. Permanent — it cannot be changed later."
        error={keyTouched && !keyValid ? 'Must match ^[a-z][a-z0-9_]{1,63}$' : undefined}
        required
      />
      <Input
        label="Title"
        value={draft.title}
        onChange={(event) => onChange({ title: event.target.value })}
        hint="The name operators see in the document list."
        required
      />
      <Select
        label="Audience"
        options={AUDIENCE_OPTIONS}
        value={draft.audience}
        onChange={(event) => onChange({ audience: event.target.value as AgreementAudience })}
        hint="Who is asked to act on this document."
      />
      <Select
        label="Required action"
        options={ACTION_OPTIONS}
        value={draft.requiredAction}
        onChange={(event) =>
          onChange({ requiredAction: event.target.value as AgreementRequiredAction })
        }
        hint="Acknowledgment and consent are distinct. A signature is a signed execution."
      />
    </div>
  );
}
